

import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { IoMdClose } from "react-icons/io";
import { FaEye, FaStar, FaWallet } from "react-icons/fa";
import { useReactToPrint } from "react-to-print";
import { IoCashOutline } from "react-icons/io5";
import { CiCreditCard1 } from "react-icons/ci";
import Loader from "../loader/Loader";
import { CartItem, Driver, Order } from "@/types/ApiTypes";
import toast from "react-hot-toast";
import { AppConfig } from "@/utils/AppConfig";
import { clearOrders } from "@/store/ordersSlice";
import { handlePrintForAllPlatforms } from "@/utils/PrintForAllPlatforms";
// import { printFromUrl } from "@/utils/helpers";


interface OrdersBodyProps {
    isNew?: boolean
    orders: Order;
    setRefetch: (refetch: boolean) => void;
    check: boolean;
    toggleSelection: (orderId: string) => void; // or whatever type is correct for this prop
    drivers?: Driver[]
}

import { useTranslation } from "react-i18next";

import { GiPresent } from "react-icons/gi";
import { formatMoney } from "@/utils/helpers";
import { RootState } from "@/store/store";
import useLockBodyScroll from "@/hooks/useLockBodyScroll";
import { clearOrdersSearchData } from "@/store/ordersSearchDataSlice";
import { useUserRole } from "@/hooks/useUserRole";



export default function OrdersBody({ orders, check, toggleSelection, isNew, setRefetch, drivers }: OrdersBodyProps) {
    const {isBranchAdmin,openAppReviewBasedOnRole} = useUserRole()
    const { t, i18n } = useTranslation();
    const locale = i18n.language;
    const [orderStatus, setOrderStatus] = useState<string>("")
    const [orderProgress, setOrderProgress] = useState<string | null>(null);
    const [timestamp, setTimestamp] = useState<number | null>(null);
    const [formattedDateEnglish, setFormattedDateEnglish] = useState("");
    const [formattedDateArabic, setFormattedDateArabic] = useState("");
    const dispatch = useDispatch();
    const baseUrl = import.meta.env.VITE_BASE_URL
    const token = useSelector((state: { auth: { token: string } }) => state.auth.token);
    const orderPrintModalRef = useRef<HTMLDivElement>(null);
    const [orderPrintModal, setOrderPrintModal] = useState(false)
    useLockBodyScroll(orderPrintModal);
    const [invoiceData, setInvoiceData] = useState<Order>()
    const checkboxRef = useRef<HTMLInputElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);
    const toastIdRef = useRef<string | undefined>(undefined);
    const [printLoading, setPrintLoading] = useState(false)
    const userData = useSelector((state: RootState) => state.user.userData)
    const [selectedDriver, setSelectedDriver] = useState<string>("");
    const [cancleModalOpen, setCancleModalOpen] = useState(false);
    const toggleCancleModal = () => {
        setCancleModalOpen(!cancleModalOpen);
    };
    const reactToPrintFn = useReactToPrint({
        contentRef,
        onBeforePrint: async () => {
            setPrintLoading(true);
            return Promise.resolve(); // or simply: return;
        },
        onAfterPrint: () => {
            setPrintLoading(false);
        },
        onPrintError: () => {
            setPrintLoading(false);
        },
    });
    useEffect(() => {
        if (printLoading) {
            // Show loading toast and store the ID
            toastIdRef.current = toast.loading(t("ordersBody.Loading") as string);
        } else {
            if (toastIdRef.current) {
                // Dismiss loading toast
                toast.dismiss(toastIdRef.current);
                toastIdRef.current = undefined;

                // Show success toast
                toast.success(t("ordersBody.Printedsuccessfully") as string);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [printLoading]);
    function openPrint() {
        if (orderPrintModal) return
        orderPrintModalRef.current?.classList.remove("hidden");
        orderPrintModalRef.current?.classList.add("flex");
        setOrderPrintModal(true)
    }
    function closePrint() {
        if (!orderPrintModal) return
        orderPrintModalRef.current?.classList.remove("flex");
        orderPrintModalRef.current?.classList.add("hidden");
        setOrderPrintModal(false)
    }

    async function cancelOrder({ id }: { id: string }) {


        try {
            const options = {
                url: `${baseUrl}/api/dashboard/orders/${id}/cancel`,
                method: "POST",
                headers: {
                    Authorization: token,
                },

            }
            const response = await axios.request(options);

            if (response.data.success == true) {
                toast.success(t("ordersBody.OrderCanceledSuccessfully") as string)
                setRefetch(true)
            }
        } catch (error) {
            console.error(error);

        } finally {

            dispatch(clearOrders());
        }
    }
    async function updateOrderStatus({ id, status }: { id: string, status: string }) {


        try {
            const options = {
                url: `${baseUrl}/api/dashboard/orders/${id}/update`,
                method: "PUT",
                headers: {
                    Authorization: token,
                },
                data: {
                    status,
                }
            }
            const response = await axios.request(options);
            if (response.data.success == true) {
                setRefetch(true)
                toast.success(t("ordersBody.OrderStatusUpdatedSuccessfully") as string)
            }
        } catch (error) {
            console.error(error);

        } finally {
            dispatch(clearOrders());
        }
    }

    async function updateOrderProgress({ id, progress }: { id: string, progress: string }) {


        try {
            const options = {
                url: `${baseUrl}/api/dashboard/orders/${id}/update?locale=${locale}`,
                method: "PUT",
                headers: {
                    Authorization: token,
                },
                data: {
                    progress,
                }
            }
            const response = await axios.request(options);
            if (response.data.success == true) {
                setRefetch(true)
                toast.success(t("ordersBody.OrderProgressUpdatedSuccessfully") as string)
            }
        } catch (error) {
            console.error(error);

        } finally {
            dispatch(clearOrders());
        }
    }
    async function getInvoice(id: string) {
        try {
            const options = {
                url: `${baseUrl}/api/dashboard/orders/${id}/invoice`,
                method: "GET",
                headers: {
                    Authorization: token,
                },
            }
            const response = await axios.request(options);

            if (response.data.success == true) {
                setInvoiceData(response.data.invoice)
            }
        } catch (error) {
            console.error(error);
        }
    }
    function formatTimestamp(timestamp: number) {
        const date = new Date(timestamp * 1000); // Convert to milliseconds

        const options: Intl.DateTimeFormatOptions = {
            year: "numeric",
            month: "long",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
        };

        // English format
        const formattedEnglish = date.toLocaleString("en-US", options);

        // Arabic format (with English numerals)
        const formattedArabicRaw = date.toLocaleString("ar-EG", options);

        const arabicNumbers = "٠١٢٣٤٥٦٧٨٩";
        const englishNumbers = "0123456789";

        const formattedArabic = formattedArabicRaw.replace(/[٠-٩]/g, (d) =>
            englishNumbers[arabicNumbers.indexOf(d)]
        );

        setFormattedDateEnglish(formattedEnglish);
        setFormattedDateArabic(formattedArabic);
    }

    useEffect(() => {
        setOrderStatus(orders.status);
        setOrderProgress(orders.progress); // Provide an empty string as default

        setTimestamp(orders.time);
        if (timestamp) {
            formatTimestamp(timestamp);
        }



    }, [timestamp, orders.status, orders.progress, orders.time]);


    useEffect(() => {

        if (check) {
            if (checkboxRef?.current) {
                checkboxRef.current.checked = true
            }
        } else {
            if (checkboxRef?.current) {
                checkboxRef.current.checked = false
            }
        }
    }, [check])

    const assignDriverToOrder = async (orderId: string, driverId: string) => {
        try {
            const response = await axios.post(
                `https://api-express.interflow.sa/api/dashboard/orders/${orderId}/assign-driver`,
                { driverId },
                {
                    headers: {
                        Authorization: token
                    }
                }
            );

            if (response.data.success) {
                toast.success(t("ordersBody.DriverAssignedSuccessfully") as string)
                setRefetch(true)
            }
        } catch (error) {
            console.error("Failed to assign driver:", error);
            throw error;
        }
    };
    useEffect(() => {
        if (orders.assignedDriver) {
            const assignedDriver = drivers?.find(driver => driver._id === orders?.assignedDriver);


            if (assignedDriver) {
                setSelectedDriver(assignedDriver._id);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orders]);

    if (!orders) {
        return <Loader />;
    }


    return (
        <>
            {cancleModalOpen && (
                <div className="fixed top-0 left-0 w-full h-full bg-black bg-opacity-50 z-[99999] flex items-center justify-center">
                    <div className="bg-white rounded-xl shadow-lg p-6 max-w-sm w-full text-center">
                        <h2 className="text-lg font-semibold mb-4">{t("cancelModal.title")}</h2>
                        <p className="text-gray-600 mb-6">{t("cancelModal.description")}</p>
                        <div className="flex justify-center gap-4">
                            <button
                                onClick={() => toggleCancleModal()}
                                className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400 transition"
                            >
                                {t("cancelModal.cancel")}
                            </button>
                            <button
                                onClick={() => {
                                    dispatch(clearOrdersSearchData())
                                    cancelOrder({ id: orders._id })
                                }}
                                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition"
                            >
                                {t("cancelModal.confirm")}
                            </button>
                        </div>
                    </div>
                </div>
            )}


            {invoiceData && <>
                <div dir="ltr" ref={contentRef} className="invoice-container text-lg font-semibold print hidden">
                    <div className="text-center flex items-center flex-col">
                        <div className="w-44 h-auto">
                            <img src={orders.invoice.logo} alt="Logo" className="w-full h-auto" />
                        </div>

                        <h1 className="text-xl font-bold flex flex-col">
                            <span>{orders.invoice.restaurantData.nameAR === orders.invoice.restaurantData.nameEN ? "" : orders.invoice.restaurantData.nameAR}</span>
                            <span>{orders.invoice.restaurantData.nameEN}</span>
                        </h1>
                        <h1 className="text-xl font-bold flex flex-col">
                            <span>{orders.invoice.branch.nameAR === orders.invoice.branch.nameEN ? "" : orders.invoice.branch.nameAR}</span>
                            <span>{orders.invoice.branch.nameEN}</span>
                        </h1>
                        <span className="font-semibold mt-1">{orders.invoice.branch.address}</span>
                        <span className="font-semibold mt-1">{orders.invoice.branch.phone}</span>

                        {orders.invoice.taxNumber && <>  <div className="w-full flex  flex-col justify-between my-2 font-semibold">
                            <span>الرقم الضريبي </span>
                            <span>Tax number</span>
                        </div>
                            <div className="flex flex-col items-center gap-2 text-lg">
                                <span className="font-bold">{orders.invoice.taxNumber}</span>
                            </div></>}

                        <div className="w-full flex flex-col justify-between my-2 font-semibold">
                            <span>فاتورة ضريبية مبسطة</span>
                            <span>Simple tax invoice</span>
                        </div>
                        <span dir="ltr" className="flex border-2  gap-2 border-black p-2 font-bold w-fit text-2xl my-3">
                            Order# <div>{orders?.invoice?.orderId}</div>
                        </span>

                        <span dir="ltr" className="font-semibold text-lg flex  gap-1">Unique orders ID# <div>{orders.invoice._id}</div></span>
                        <span className="font-semibold text-md flex gap-1 items-center ">Printed at : <div>{new Date().toLocaleString("en-US")}</div></span>
                        <div className="font-semibold text-md flex items-center gap-1">
                            <span>  Order time : </span>
                            <span >{orders.invoice.deliverTime}</span>
                            <span> : وقت الطلب  </span>
                        </div>
                    </div>

                    <div className="w-full border-b-2 border-b-black" />

                    <div className="font-semibold space-y-1">
                        <div className="text-center font-semibold">
                            {/* English Delivery Type */}
                            <div>
                                {orders.deliveryType === "receiving"
                                    ? "Pickup"
                                    : orders.deliveryType === "dine-in"
                                        ? "Dine in"
                                        : "Cashier"}
                            </div>



                        </div>

                        {/* Dine-In Table Info (Both languages) */}
                        {orders.deliveryType === "dine-in" && (
                            <div className="flex justify-between text-sm">
                                <div>
                                    Floor {orders.dineIn?.floor.number} - Table {orders.dineIn?.table.number}
                                </div>
                                <div dir="rtl">
                                    طابق {orders.dineIn?.floor.number} - طاولة {orders.dineIn?.table.number}
                                </div>
                            </div>
                        )}
                    </div>


                    <div className="w-full border-b-2 border-b-black" />

                    <div className=" space-y-1">
                        <div className="flex justify-between font-bold" dir="rtl">
                            <div className="text-start">Price</div>
                            <div className="flex gap-5">
                                <span>item</span>
                                <span>Qty</span>
                            </div>
                        </div>
                        <div className="my-2  w-full border-b-2 border-b-black" />
                        <div className=" font-semibold">
                            {orders.invoice.items.map((item: CartItem, index) => {
                                return (
                                    <div key={index} className="flex flex-col gap-1">
                                        <div className="flex items-center gap-10  justify-between">
                                            <span className="whitespace-normal flex flex-row-reverse gap-5 " dir="rtl" ><span>{item.quantity} </span>
                                                <span>
                                                    <span className="flex flex-col gap-1">{item.nameEN}</span>
                                                    <span>{item.nameAR === item.nameEN ? "" : item.nameAR}</span>
                                                </span>
                                            </span>
                                            <span dir="ltr" className="flex items-center gap-1 self-start "> {AppConfig.currency.icon()}{formatMoney(item.totalPriceWithTaxes)}</span>
                                        </div>
                                        {item.addons.length > 0 && (
                                            <span className="flex flex-col font-bold">
                                                <span className="text-center">{t("orders.addons")}</span>
                                                <span className="font-normal flex flex-col gap-1 w-full">
                                                    {item.addons.map((addon, i) => {
                                                        const hasPrice = addon.price && addon.price > 0;
                                                        return hasPrice ? (
                                                            <div key={i} className="flex justify-between w-full text-sm">
                                                                <div></div>
                                                                <span className="text-right">{addon.nameEN}|{addon.nameAR}</span>
                                                                <span dir="ltr">
                                                                    {AppConfig.currency.icon()} {formatMoney(addon.price)}
                                                                </span>
                                                            </div>
                                                        ) : (
                                                            <div key={i} className="flex justify-center w-full text-sm">
                                                                <span className="text-right">{addon.nameEN}|{addon.nameAR}</span>
                                                            </div>
                                                        );
                                                    })}
                                                </span>
                                            </span>
                                        )}


                                        {item.options.length > 0 && (
                                            <span className="flex flex-col font-bold">
                                                <span className="text-center">{t("ordersBody.Options")}</span>
                                                <span className="font-normal flex flex-col gap-1 w-full">
                                                    {item.options.map((option, i) =>
                                                        option.options.map((opt, j) => {
                                                            const hasPrice = opt.price && opt.price > 0;
                                                            return hasPrice ? (
                                                                <div key={`${i}-${j}`} className="flex justify-between w-full text-sm">
                                                                    <div></div> {/* Optional for alignment */}
                                                                    <span className="text-right">{opt.nameEN}|{opt.nameAR}</span>
                                                                    {opt.price && opt.price > 0 && (<span dir="ltr">
                                                                        {AppConfig.currency.icon()} {formatMoney(opt.price)}
                                                                    </span>)}
                                                                </div>
                                                            ) : (
                                                                <div key={`${i}-${j}`} className="flex justify-center w-full text-sm">
                                                                    <span className="text-right">{opt.nameEN}|{opt.nameAR}</span>
                                                                </div>
                                                            );
                                                        })
                                                    )}
                                                </span>
                                            </span>
                                        )}

                                        {orders.invoice.items.length - 1 !== index && <div className="w-full border-b-2 border-b-black" />}
                                    </div>


                                )
                            })}
                        </div>


                    </div>


                    {orders.invoice.subtotal > 0 && (
                        <>
                            <div className="col-span-full w-full border-b-2 border-b-black" />
                            <div dir="ltr" className="flex font-semibold w-[80%] ml-auto items-center justify-between">
                                <div className="flex flex-col text-end w-[50%]">
                                    <div>Sub total</div>
                                    <div>المجموع الفرعي</div>
                                </div>
                                <div className=" text-center">
                                    {AppConfig.currency.icon()} {formatMoney(orders.invoice.subtotal)}
                                </div>
                            </div>
                        </>
                    )}

                    {orders.invoice.discount > 0 && (
                        <div dir="ltr" className="flex font-semibold w-[80%] ml-auto items-center justify-between ">
                            <div className="flex flex-col text-end w-[50%]">
                                <div>Discount</div>
                                <div>الخصم</div>
                            </div>
                            <div className=" text-center">
                                {AppConfig.currency.icon()} {formatMoney(orders.invoice.discount)}
                            </div>
                        </div>
                    )}

                    {orders.usedWalletBalance > 0 && (
                        <div dir="ltr" className="flex font-semibold w-[80%] ml-auto items-center justify-between ">
                            <div className="flex flex-col text-end w-[50%]">
                                <div>Used Wallet Balance</div>
                                <div>رصيد المحفظه المستخدم</div>
                            </div>
                            <div className=" text-center">
                                {AppConfig.currency.icon()} {formatMoney(orders.usedWalletBalance)}
                            </div>
                        </div>
                    )}
                    {orders.pointsDiscount && orders.pointsDiscount > 0 && (
                        <div dir="ltr" className="flex font-semibold w-[80%] ml-auto items-center justify-between">
                            <div className="flex flex-col text-end w-[50%]">
                                <div>Points Discount</div>
                                <div>خصم النقاط</div>
                            </div>
                            <div className="text-center flex items-center gap-1 ">
                                {AppConfig.currency.icon()} {formatMoney(orders.pointsDiscount)}
                            </div>
                        </div>
                    )}


                    {orders.invoice.taxes > 0 && (
                        <div dir="ltr" className="flex font-semibold w-[80%] ml-auto items-center justify-between ">
                            <div className="flex flex-col text-end w-[50%]">
                                <div>Taxes (15%)</div>
                                <div>الضرايب (15%)</div>
                            </div>
                            <div className=" text-center">
                                {AppConfig.currency.icon()} {formatMoney(orders.invoice.taxes)}
                            </div>
                        </div>
                    )}
                    {orders.invoice.serviceFees && orders.invoice.serviceFees > 0 && (
                        <div
                            dir="ltr"
                            className="ml-auto mt-2 flex w-[80%] items-center justify-between font-semibold"
                        >
                            <div className="flex w-[50%] flex-col text-end">
                                <div>Service Fees</div>
                                <div>رسوم الخدمة</div>
                            </div>
                            <div className="text-center">
                                {AppConfig.currency.icon()} {formatMoney(orders.serviceFees || 0)}
                            </div>
                        </div>
                    )}
                    {orders.invoice.total > 0 && (
                        <>
                            <div className="col-span-full w-full border-b-2 border-b-black mt-2" />
                            <div dir="ltr" className="flex font-semibold w-[80%] ml-auto items-center justify-between ">
                                <div className="flex flex-col text-end w-[50%]">
                                    <div>Total</div>
                                    <div>المجموع</div>
                                </div>
                                <div className=" text-center">
                                    {AppConfig.currency.icon()} {formatMoney(orders.invoice.total)}
                                </div>
                            </div>
                        </>
                    )}

                    <div className="col-span-full w-full border-b-2 border-b-black mt-2" />

                    <div dir="ltr" className="flex font-semibold w-[80%] ml-auto items-center justify-between ">
                        <div className="flex flex-col text-end w-[50%]">
                            <div>No. Products</div>
                            <div>عدد المنتجات</div>
                        </div>
                        <div className=" text-center">
                            {orders.invoice.totalItems}
                        </div>
                    </div>

                    <div className="col-span-full w-full border-b-2 border-b-black mt-2" />

                    <div dir="ltr" className="flex  font-bold text-sm items-center justify-between w-full ml-auto ">
                        <div className=" text-center">
                            {orders.paymentMethod}
                        </div>
                        <div className="flex flex-col  text-end ">
                            <div>Payment method</div>
                            <div>طريقة الدفع</div>
                        </div>
                    </div>

                    <div className="text-center flex items-center justify-center flex-col">
                        <div className="w-52 h-52 ">
                            <img
                                src="https://upload.wikimedia.org/wikipedia/commons/d/d0/QR_code_for_mobile_English_Wikipedia.svg"
                                alt="QR"
                                className="w-full h-full object-contain"
                            />
                        </div>
                    </div>
                </div>

            </>}


            {orders && <>
                <div
                    ref={orderPrintModalRef}
                    className="absolute hidden z-[9999]  px-2  top-0 left-0 w-full h-full   justify-center items-center">
                    <div onClick={closePrint} className="absolute top-0 left-0 w-full h-full bg-black bg-opacity-50 z-[9991]"></div>
                    {invoiceData ? <><div className="z-[9999]  gap-2 flex-col flex md:w-1/2 lg:w-[600px] w-full overflow-auto max-h-[90vh] bg-white text-black rounded-lg p-5">
                        <div onClick={closePrint} className=" text-xl self-end text-red-600 ">
                            <IoMdClose className="cursor-pointer" />
                        </div>

                        <div className="flex border px-2 py-5  justify-between">
                            <h2>{t("ordersBody.phone")}</h2>
                            {orders?.customer?.phone !== null ? orders?.customer?.phone : t("ordersBody.Deleted")}
                        </div>
                        {orders?.deliveryType === "delivery" && <> <div className="flex border px-2 py-5 md:hidden justify-between">
                            <div>{t("orders.driverName")}</div>

                            {orders?.deliveryType === "delivery" ? (() => {
                                const isInactive = orders.status === "cancelled" || orders.status === "rejected";
                                const branchDrivers = drivers?.filter(
                                    (driver) => driver.branchId === orders.branch.id
                                ) || [];

                                if (isInactive) {
                                    const assignedDriver = drivers?.find(driver => driver._id === orders.assignedDriver);
                                    return (
                                        <span className="text-gray-600">
                                            {assignedDriver?.username || t("ordersBody.noDriversAvailable")}
                                        </span>
                                    );
                                }

                                return branchDrivers.length > 0 ? (
                                    <select
                                        name="driver"
                                        id="driver"
                                        value={selectedDriver || ""}
                                        onChange={(e) => {
                                            if (e.target.value === "") return;
                                            assignDriverToOrder(orders._id, e.target.value);
                                            setSelectedDriver(e.target.value);
                                        }}
                                        className="border px-3 py-1 rounded-md"
                                    >
                                        <option value="">{t("orders.driverName")}</option>
                                        {branchDrivers.map((driver) => (
                                            <option key={driver._id} value={driver._id}>
                                                {driver.username}
                                            </option>
                                        ))}
                                    </select>
                                ) : (
                                    <span className="text-gray-400 italic">
                                        {t("ordersBody.noDriversAvailable")}
                                    </span>
                                );
                            })() : (
                                "--"
                            )}
                        </div></>}

                        <div className="flex border px-2 py-5 items-center justify-center">
                            <div className="flex items-center justify-center flex-col">
                                <span className="h-full font-semibold"> {orders.deliveryType === "receiving" ? t("ordersBody.Receiving") : orders.deliveryType === "dine-in" ? t("ordersBody.DineIn") : orders.deliveryType === "delivery" && t("ordersBody.delivery")}</span>

                                {orders.deliveryType === "dine-in" && <span> {t("ordersBody.Floor")}{orders.dineIn?.floor.number} - {t("ordersBody.Table")} {orders.dineIn?.table.number}</span>}
                                {orders.deliveryType === "delivery" && orders.address && (
                                    <div className="text-sm leading-relaxed mt-3 flex flex-col items-center text-end whitespace-pre-line text-gray-800">

                                        <div>
                                            {t("address.city")} - {orders.address.city}
                                        </div>
                                        <div>
                                            {t("address.street")} - {orders.address.street}
                                        </div>
                                        <div>
                                            {t("address.building")} - {orders.address.buildingNumber}
                                        </div>
                                        <div>
                                            {t("address.floor")} - {orders.address.floorNumber}
                                        </div>
                                        <div>
                                            {t("address.apartment")} - {orders.address.apartmentNumber}
                                        </div>
                                        {orders.address?.additionalInfo && (
                                            <div className="mt-3 text-xs  text-gray-500 text-center">
                                                <h1 className="font-bold mb-2">  {t("address.info")}</h1>
                                                {orders.address.additionalInfo}
                                            </div>
                                        )}
                                    </div>
                                )}



                            </div>
                        </div>
                        <div className="flex border px-2 py-5 justify-between items-center ">
                            <h2>{t("orders.receiptTime")}</h2>
                            <div className="flex items-center justify-center flex-col gap-1">
                                {orders.deliveryTiming === "afap" ? t("ordersBody.AsSoonAsPossible") : orders.deliveryTiming === "scheduled" ? t("ordersBody.Scheduled") : t("ordersBody.AsSoonAsPossible")}
                                {orders.deliveryAt && <span className="flex gap-1 items-center"> <span>{t("ordersBody.ReceiveAt")}</span> <span className="pt-1">{orders.deliveryAt}</span></span>}
                            </div>
                        </div>
                        <div className="flex border px-2  justify-between items-center md:hidden ">
                            <span>{t("ordersBody.OrderStatus")}</span>
                            <div className=" py-4 ">
                                {orderStatus === "accepted" && orderProgress === "delivering" ? (
                                    <div className="w-fit mx-auto">
                                        <span className="px-2 py-[2px] rounded-xl text-white bg-yellow-500">
                                            {t("ordersBody.delivering")}
                                        </span>
                                    </div>) : orderStatus === "pending" && orderProgress === null ? (
                                        <div className="w-fit px-3 py-1 mx-auto rounded-2xl bg-blue-400 text-white">
                                            {t("ordersBody.pending")}
                                        </div>
                                    ) : orderStatus === "accepted" && orderProgress === null ? (
                                        <div className="border mx-auto px-2 py-[2px] rounded-xl w-fit">
                                            <select
                                                value={orderProgress || ""}
                                                onChange={(e) => {
                                                    const val = e.target.value;
                                                    if (val === "") {
                                                        // ignore placeholder selection
                                                        return;
                                                    }
                                                    if (val === "cancelled") {
                                                        cancelOrder({ id: orders?._id });
                                                        setOrderProgress(val);
                                                        setOrderStatus(val);
                                                        return;
                                                    }
                                                    setOrderProgress(val);
                                                    updateOrderProgress({ id: orders?._id, progress: val });
                                                }}
                                            >
                                                <option value="">{t("ordersBody.accepted")}</option>
                                                <option value="preparing">{t("ordersBody.preparing")}</option>
                                                <option value="finished">{t("ordersBody.finished")}</option>
                                                <option value="delivered">{t("ordersBody.delivered")}</option>
                                                <option value="cancelled" className="text-red-400 font-semibold">
                                                    {t("ordersBody.cancelorders")}
                                                </option>
                                            </select>
                                        </div>
                                    ) : orderStatus === "rejected" ? (
                                        <div className="bg-red-500 px-2 py-[2px] mx-auto rounded-xl w-fit text-white">
                                            {t("ordersBody.rejected")}
                                        </div>
                                    ) : orderStatus === "cancelled" ? (
                                        <div className="bg-red-500 px-2 py-[2px] mx-auto rounded-xl w-fit text-white">
                                            {t("ordersBody.Cancelled")}
                                        </div>
                                    ) : orderStatus === "returned" ? (
                                        <div className="bg-red-500 px-2 py-[2px] mx-auto rounded-xl w-fit text-white">
                                            {t("ordersBody.returned")}
                                        </div>
                                    ) : orderProgress === "preparing" ||
                                        orderProgress === "finished" ? (
                                    <div className="border px-2 py-[2px] mx-auto rounded-xl w-fit">
                                        <select
                                            value={orderProgress || ""}
                                            onChange={(e) => {
                                                const val = e.target.value;
                                                if (val === "") {
                                                    return;
                                                }
                                                if (val === "cancelled") {
                                                    cancelOrder({ id: orders?._id });
                                                    setOrderProgress(val);
                                                    setOrderStatus(val);
                                                    return;
                                                }
                                                setOrderProgress(val);
                                                updateOrderProgress({ id: orders?._id, progress: val });
                                            }}
                                        >
                                            <option value="">{t("ordersBody.accepted")}</option>
                                            <option value="preparing">{t("ordersBody.preparing")}</option>
                                            <option value="finished">{t("ordersBody.finished")}</option>
                                            <option value="cancelled" className="text-red-400 font-semibold">
                                                {t("ordersBody.cancelorders")}
                                            </option>
                                        </select>
                                    </div>
                                ) : orderProgress === "delivered" ? (
                                    <div className="w-fit mx-auto">
                                        <span className="px-2 py-[2px] rounded-xl text-white bg-green-600">
                                            {t("ordersBody.delivered")}
                                        </span>
                                    </div>
                                ) : null}

                            </div>
                        </div>
                        <div className="flex border px-2 py-5 justify-between">
                            <h2>{t("ordersBody.OrderNumber")}</h2>
                            <span>{orders?._id}</span>
                        </div>
                        <div className="flex border px-2 py-5 justify-between">
                            <h2>{t("ordersBody.Branch")}</h2>
                            <span>  {t(orders?.branch.nameEN, orders?.branch.nameAR)}</span>
                        </div>
                        <div className="flex flex-col border px-2 py-5 gap-3">
                            <h2 className="font-bold">{t("ordersBody.OrderDetails")}</h2>
                            <div className={`flex flex-col text-[13px] gap-1 `}>
                                {orders?.items.map((item: CartItem, index) => {
                                    return (

                                        <div key={index} className={`flex flex-col gap-1  py-1 ${orders.items.length - 1 !== index && "border-b"}`}  >
                                            <span className="font-bold" dir="ltr">{t(item.nameEN, item.nameAR)} X {item.quantity} </span>
                                            {item.notes && <div className="flex flex-col">
                                                <span className="text-[14px] font-semibold">{t("ordersBody.ItemNote")}</span>
                                                <span className="whitespace-pre-wrap break-words ">
                                                    {item.notes && item.notes}
                                                </span>
                                            </div>}
                                            {item.addons.length > 0 && <span className="flex flex-col ">{t("ordersBody.Addons")}   <span className="font-normal flex flex-col">{item.addons?.map((addon, i) => <span key={i}>{t(addon.nameEN, addon.nameAR)}</span>)}</span></span>}
                                            {item.options.length > 0 && <span className="flex flex-col "> {t("ordersBody.Options")}<span className="flex flex-col font-normal" >{item.options.map((option) => option.options.map((item, i) => <span key={i}>{t(item.nameEN, item.nameAR)}</span>))}</span></span>}
                                        </div>

                                    )
                                })}
                            </div>
                            <div>
                                {orders.notes && <div className="flex flex-col">
                                    <span className="text-[14px] font-semibold">{t("ordersBody.OrderNote")}</span>
                                    <span className="whitespace-pre-wrap break-words">
                                        {orders.notes && orders.notes}
                                    </span>
                                </div>}
                            </div>
                        </div>
                        <div className="flex border gap-3 flex-col py-3 px-2">
                            {orders?.usedWalletBalance > 0 && <>
                                <div className="flex justify-between">
                                    <h1>{t("ordersBody.UsedWalletBalance")}</h1>
                                    <span dir="rtl" className="flex items-center gap-1 text-green-600">{formatMoney(orders?.usedWalletBalance)} {AppConfig.currency.icon()}</span>
                                </div>
                            </>}
                            {orders?.couponDiscount > 0 && <>
                                <div className="flex justify-between">
                                    <h1>{t("ordersBody.UsedCouponDiscount")}</h1>
                                    <span dir="rtl" className="flex items-center gap-1 text-green-600">{formatMoney(orders?.couponDiscount)} {AppConfig.currency.icon()}</span>
                                </div>
                            </>}
                            {orders.pointsDiscount && orders.pointsDiscount > 0 && <>
                                <div className="flex justify-between gap-1 md:hidden text-[12px]">
                                    <h1 className="flex items-center gap-1">{t("ordersBody.points")} </h1>
                                    <span dir="rtl" className="flex items-center gap-1 text-green-600"> ({orders?.usedPoints} <GiPresent size={17} className="mb-1" />  = {orders?.pointsDiscount} {AppConfig.currency.icon()})  </span>
                                </div>
                            </>}
                            <div className="flex justify-between">
                                <h2>{t("ordersBody.Total")}</h2>
                                <div className="flex gap-2">
                                    <span dir="rtl">{i18n.language && orders?.total}
                                        {AppConfig.currency.icon()}</span>
                                    <span className={`${orders?.paid === true ? "text-green-600" : "text-red-600"}`}>{orders?.paid === true ? t("ordersBody.Paid") : orders.status === "returned" ? t("ordersBody.returned") : t("ordersBody.Unpaid")}</span>
                                </div>
                            </div>
                        </div>
                        {orders.review && <div className="flex border gap-3 flex-col py-3 px-2">
                            <div className="flex justify-between">
                                <span>{t("ordersBody.rating")}</span>

                                <span className="flex items-center gap-1">{orders.review.rating} <FaStar className="text-yellow-400" /></span>

                            </div>
                            {orders.review.comment && <span className=" text-gray-500 break-all whitespace-pre-wrap">{orders.review.comment}</span>}

                            {orders.review.driverRating && <div className="flex justify-between">
                                <span>{t("ordersBody.driverRating")}</span>
                                <span className="flex items-center gap-1">{orders.review.driverRating} <FaStar className="text-yellow-400" /></span>
                            </div>}
                            {orders.review.driverComment && <span className=" text-gray-500 break-all whitespace-pre-wrap">{orders.review.driverComment}</span>}
                        </div>}
                        <div className="flex justify-end items-center gap-3">
                            {orders.cancellable && orders.status !== "cancelled" && orders.status !== "returned" ? (
                                <button onClick={toggleCancleModal} className="bg-red-600 text-white w-fit px-5 py-2 rounded-lg">
                                    {t("cancelModal.cancelOrder")}
                                </button>
                            ) : null}
                            <button onClick={() => handlePrintForAllPlatforms(reactToPrintFn, contentRef)} className="my-btn w-fit px-5 py-2 self-center">{t("ordersBody.print")}</button>
                            {orderStatus === "pending" && <div className="flex md:hidden" >
                                <div className="flex  items-center text-[12px] gap-2">
                                    <span
                                        onClick={() => {

                                            setOrderStatus("accepted")
                                            updateOrderStatus({ id: orders?._id, status: "approve" })
                                        }}
                                        className="px-2 py-2  rounded-md w-16 transition-colors duration-200 text-white cursor-pointer text-center hover:bg-green-700 bg-green-600">{t("ordersBody.Accept")}</span>
                                    <span
                                        onClick={() => {

                                            setOrderStatus("rejected")
                                            updateOrderStatus({ id: orders?._id, status: "reject" })
                                        }}
                                        className="px-2  py-2 rounded-md w-16 transition-colors duration-200 text-white cursor-pointer text-center hover:bg-red-700 bg-red-600">{t("ordersBody.Reject")}</span>
                                </div>
                            </div>}
                        </div>
                    </div></> : <Loader />}
                </div>

                <tr className={`border-b ${isNew ? "bg-green-100" : "bg-white"}`}>
                    <td className="px-6 py-4">
                        <input
                            onChange={() => toggleSelection(orders?._id)}

                            ref={checkboxRef} type="checkbox" />
                    </td>
                    <td className="px-6 py-4">
                        {orders?.customer?.name !== null ? orders?.customer?.name : t("ordersBody.Deleted")}
                    </td>

                    <td className="px-6 py-4 hidden md:flex flex-col ">
                        {orders?._id}
                        <span >{t("ordersBody.Order")}# {orders?.invoice?.orderId}</span>
                    </td>
                    {userData?.role === "owner" || userData?.role === "superadmin" ? (
                        <td className="px-6 py-4 hidden md:table-cell">
                            {t(orders?.invoice.branch.nameEN, orders?.invoice.branch.nameAR)}
                        </td>
                    ) : null}


                    <td className="px-6 py-4 hidden md:table-cell">
                        <span className="h-full font-semibold"> {orders.deliveryType === "receiving" ? t("ordersBody.Receiving") : orders.deliveryType === "dine-in" ? t("ordersBody.DineIn") : orders.deliveryType === "delivery" && t("ordersBody.delivery")}</span>

                    </td>
                    <td className="px-6 py-4 hidden md:table-cell">
                        {orders?.deliveryType === "delivery" ? (() => {
                            const isInactive = orders.status === "cancelled" || orders.status === "rejected";
                            const branchDrivers = drivers?.filter(
                                (driver) => driver.branchId === orders.branch.id
                            ) || [];

                            if (isInactive) {
                                const assignedDriver = drivers?.find(driver => driver._id === orders.assignedDriver);
                                return (
                                    <span className="text-gray-600">
                                        {assignedDriver?.username || t("ordersBody.noDriversAvailable")}
                                    </span>
                                );
                            }

                            return branchDrivers.length > 0 ? (
                                <select
                                    name="driver"
                                    id="driver"
                                    value={selectedDriver || ""}
                                    onChange={(e) => {
                                        if (e.target.value === "") return;
                                        assignDriverToOrder(orders._id, e.target.value);
                                        setSelectedDriver(e.target.value);
                                    }}
                                    className="border px-3 py-1 rounded-md"
                                >
                                    <option value="">{t("orders.driverName")}</option>
                                    {branchDrivers.map((driver) => (
                                        <option key={driver._id} value={driver._id}>
                                            {driver.username}
                                        </option>
                                    ))}
                                </select>
                            ) : (
                                <span className="text-gray-400 italic">
                                    {t("ordersBody.noDriversAvailable")}
                                </span>
                            );
                        })() : (
                            "--"
                        )}
                    </td>


                    <td className="px-6 py-4 hidden md:table-cell">
                        <div className="  mx-auto ">
                            {t(formattedDateEnglish, formattedDateArabic)}
                        </div>
                    </td>
                    {/* 'creditcard' | 'applepay' | 'cash' */}
                    <td className="px-6 py-4 hidden md:table-cell">
                        <div className="flex flex-col justify-center h-full items-center gap-2">
                            <span className="flex items-center">
                                {orders?.paymentMethod === "cash" ? t("ordersBody.Cash") : orders?.paymentMethod === "applepay" ? t("ordersBody.ApplePay") : t("ordersBody.CreditCard")}
                                {orders?.paymentMethod === "cash" ? <><IoCashOutline className="text-green-700 text-lg" /></> : <><CiCreditCard1 className="text-blue-700 text-lg" /></>}
                            </span>
                            {orders?.usedWalletBalance > 0 && <>
                                <div className="flex justify-between gap-1 text-[12px]">
                                    <h1 className="flex items-center gap-1">{t("ordersBody.DiscountFromWallet")} <FaWallet /></h1>
                                    <span dir="rtl" className="flex items-center gap-1 text-green-600">{formatMoney(orders?.usedWalletBalance)} {AppConfig.currency.icon()}</span>
                                </div>
                            </>}
                            {orders.pointsDiscount && orders.pointsDiscount > 0 && <>
                                <div className="flex justify-between gap-1 text-[12px]">
                                    <h1 className="flex items-center gap-1">{t("ordersBody.points")} </h1>
                                    <span dir="rtl" className="flex items-center gap-1 text-green-600"> ({orders?.usedPoints} <GiPresent size={17} className="mb-1" />  = {formatMoney(orders?.pointsDiscount)} {AppConfig.currency.icon()})  </span>
                                </div>
                            </>}
                        </div>
                    </td>
                    <td className="py-4 hidden md:table-cell">
                        {orderStatus === "accepted" && orderProgress === "delivering" ? (
                            <div className="w-fit mx-auto">
                                <span className="px-2 py-[2px] rounded-xl text-white bg-yellow-500">
                                    {t("ordersBody.delivering")}
                                </span>
                            </div>) : orderStatus === "pending" && orderProgress === null ? (
                                <div className="w-fit px-3 py-1 mx-auto rounded-2xl bg-blue-400 text-white">
                                    {t("ordersBody.pending")}
                                </div>
                            ) : orderStatus === "accepted" && orderProgress === null ? (
                                <div className="border mx-auto px-2 py-[2px] rounded-xl w-fit">
                                    <select
                                        value={orderProgress || ""}
                                        onChange={(e) => {
                                            const val = e.target.value;
                                            if (val === "") {
                                                // ignore placeholder selection
                                                return;
                                            }
                                            if (val === "cancelled") {
                                                cancelOrder({ id: orders?._id });
                                                setOrderProgress(val);
                                                setOrderStatus(val);
                                                return;
                                            }
                                            setOrderProgress(val);
                                            updateOrderProgress({ id: orders?._id, progress: val });
                                        }}
                                    >
                                        <option value="">{t("ordersBody.accepted")}</option>
                                        <option value="preparing">{t("ordersBody.preparing")}</option>

                                        <option value="finished">{t("ordersBody.finished")}</option>
                                        <option value="delivered">{t("ordersBody.delivered")}</option>
                                        <option value="cancelled" className="text-red-400 font-semibold">
                                            {t("ordersBody.cancelorders")}
                                        </option>
                                    </select>
                                </div>
                            ) : orderStatus === "rejected" ? (
                                <div className="bg-red-500 px-2 py-[2px] mx-auto rounded-xl w-fit text-white">
                                    {t("ordersBody.rejected")}
                                </div>
                            ) : orderStatus === "cancelled" ? (
                                <div className="bg-red-500 px-2 py-[2px] mx-auto rounded-xl w-fit text-white">
                                    {t("ordersBody.Cancelled")}
                                </div>
                            ) : orderStatus === "returned" ? (
                                <div className="bg-red-500 px-2 py-[2px] mx-auto rounded-xl w-fit text-white">
                                    {t("ordersBody.returned")}
                                </div>
                            ) : orderProgress === "preparing" ||
                                orderProgress === "finished" ? (
                            <div className="border px-2 py-[2px] mx-auto rounded-xl w-fit">
                                <select
                                    value={orderProgress || ""}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        if (val === "") {
                                            return;
                                        }
                                        if (val === "cancelled") {
                                            cancelOrder({ id: orders?._id });
                                            setOrderProgress(val);
                                            setOrderStatus(val);
                                            return;
                                        }
                                        setOrderProgress(val);
                                        updateOrderProgress({ id: orders?._id, progress: val });
                                    }}
                                >
                                    <option value="">{t("ordersBody.accepted")}</option>
                                    <option value="preparing">{t("ordersBody.preparing")}</option>
                                    <option value="finished">{t("ordersBody.finished")}</option>
                                    <option value="delivered">{t("ordersBody.delivered")}</option>
                                    <option value="cancelled" className="text-red-400 font-semibold">
                                        {t("ordersBody.cancelorders")}
                                    </option>
                                </select>
                            </div>
                        ) : orderProgress === "delivered" ? (
                            <div className="w-fit mx-auto">
                                <span className="px-2 py-[2px] rounded-xl text-white bg-green-600">
                                    {t("ordersBody.delivered")}
                                </span>
                            </div>
                        ) : null}
                    </td>

                    <td className="px-6  flex-col  py-4 hidden md:flex">
                        <span dir="rtl">{formatMoney(orders?.total)}
                            {AppConfig.currency.icon()}</span>
                        <span className={`${orders?.paid === true ? "text-green-600" : "text-red-600"}`}>{orders?.paid === true ? t("ordersBody.Paid") : orders.status === "returned" ? t("ordersBody.returned") : t("ordersBody.Unpaid")}</span>
                    </td>
                    {orderStatus === "pending" ? <>
                        <td className=" ">
                            <div className="flex  flex-col items-center text-[12px] gap-1">
                                <span
                                    onClick={() => {

                                        setOrderStatus("accepted")
                                        updateOrderStatus({ id: orders?._id, status: "approve" })
                                    }}
                                    className="px-2 py-[2px] hidden md:block rounded-xl w-16 transition-colors duration-200 text-white cursor-pointer text-center hover:bg-green-700 bg-green-600">{t("ordersBody.accepted")}</span>
                                <span
                                    onClick={() => {
                                        openPrint()
                                        getInvoice(orders?._id)
                                    }}
                                    className="px-2 py-[2px] rounded-xl w-16 transition-colors duration-200 text-white cursor-pointer text-center hover:bg-blue-700 bg-blue-600 text-[10px] flex justify-center items-center gap-1">{t("ordersBody.Preview")}<FaEye className="text-sm" /></span>
                                <span
                                    onClick={() => {

                                        setOrderStatus("rejected")
                                        updateOrderStatus({ id: orders?._id, status: "reject" })
                                    }}
                                    className="px-2 py-[2px] hidden md:block rounded-xl w-16 transition-colors duration-200 text-white cursor-pointer text-center hover:bg-red-700 bg-red-600">{t("ordersBody.Reject")}</span>
                            </div>
                        </td>
                    </> : orderProgress === "preparing" || orderProgress === "finished" || orderProgress === "delivered" || orderStatus === "accepted" || orderStatus === "rejected" || orderStatus === "cancelled" ? <>
                        <td className=" py-4 ">
                            <span
                                onClick={() => {
                                    openPrint()
                                    getInvoice(orders?._id)
                                    if(isBranchAdmin){
                                        openAppReviewBasedOnRole()
                                    }
                                }}
                                className="px-2 py-[2px] rounded-xl w-16 transition-colors duration-200 text-white cursor-pointer text-center hover:bg-blue-700 bg-blue-600 text-[10px] flex justify-center items-center mx-auto gap-1">{t("ordersBody.Preview")}<FaEye className="text-sm" /></span>

                        </td>
                    </> : null}
                </tr ></>}
        </>
    )
}
